import React from 'react';
import { Badge } from '@vehix/portal/components/ui/badge';
import {
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  SkipForward,
  Ban,
  AlertTriangle
} from 'lucide-react';
import type { PipelineStatus, StepStatus } from '../../services/pipelineOrchestrator';

type StatusValue = PipelineStatus | StepStatus;

interface StatusConfig {
  label: string;
  icon: React.ElementType;
  className: string;
}

const STATUS_MAP: Record<string, StatusConfig | undefined> = {
  pending: {
    label: 'Pending',
    icon: Clock,
    className: 'bg-muted text-muted-foreground border-muted'
  },
  running: {
    label: 'Running',
    icon: Loader2,
    className: 'bg-blue-500/15 text-blue-500 border-blue-500/30'
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    className: 'bg-green-500/15 text-green-500 border-green-500/30'
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    className: 'bg-red-500/15 text-red-500 border-red-500/30'
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-orange-500/15 text-orange-500 border-orange-500/30'
  },
  partial_success: {
    label: 'Partial',
    icon: AlertTriangle,
    className: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/30'
  },
  skipped: {
    label: 'Skipped',
    icon: SkipForward,
    className: 'bg-muted/50 text-muted-foreground/60 border-muted/50'
  }
};

interface PipelineStatusBadgeProps {
  status: StatusValue;
  size?: 'sm' | 'default';
}

export const PipelineStatusBadge: React.FC<PipelineStatusBadgeProps> = ({
  status,
  size = 'default'
}) => {
  const config = STATUS_MAP[status] ?? STATUS_MAP['pending']!;
  const Icon = config.icon;
  const isRunning = status === 'running';

  return (
    <Badge variant='outline' className={`${config.className} gap-1.5 font-medium`}>
      <Icon
        className={`${size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} ${isRunning ? 'animate-spin' : ''}`}
      />
      <span className={size === 'sm' ? 'text-xs' : 'text-xs'}>{config.label}</span>
    </Badge>
  );
};

/**
 * Label, icon and badge classes for a status. Exported so other surfaces (the
 * stage strip, the steps table) render statuses identically to the badge
 * instead of re-deriving their own colour vocabulary.
 */
export function getStatusConfig(status: StatusValue): StatusConfig {
  return STATUS_MAP[status] ?? STATUS_MAP['pending']!;
}

/**
 * Returns the border color class for a given status (used on DAG nodes).
 */
export function getStatusBorderColor(status: StatusValue): string {
  switch (status) {
    case 'running':
      return 'border-blue-500';
    case 'completed':
      return 'border-green-500';
    case 'failed':
      return 'border-red-500';
    case 'cancelled':
      return 'border-orange-500';
    case 'partial_success':
      return 'border-yellow-500';
    case 'skipped':
      return 'border-muted';
    default:
      return 'border-border';
  }
}

/**
 * Returns the text color class for a given status (used on bare icons, where
 * the full badge treatment would be too heavy).
 */
export function getStatusTextColor(status: StatusValue): string {
  switch (status) {
    case 'running':
      return 'text-blue-500';
    case 'completed':
      return 'text-green-500';
    case 'failed':
      return 'text-red-500';
    case 'cancelled':
      return 'text-orange-500';
    case 'partial_success':
      return 'text-yellow-500';
    default:
      return 'text-muted-foreground';
  }
}

/**
 * Returns the edge stroke color for a given status (used on DAG edges).
 */
export function getStatusEdgeColor(status: StatusValue): string {
  switch (status) {
    case 'running':
      return '#3b82f6';
    case 'completed':
      return '#22c55e';
    case 'failed':
      return '#ef4444';
    case 'cancelled':
      return '#f97316';
    case 'partial_success':
      return '#eab308';
    case 'skipped':
      return '#6b7280';
    default:
      return '#374151';
  }
}
